import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toNewsItems } from "../../../lib/articleAdapter";
import { matchesCategory } from "../../../lib/categoryAliases";
import { filterBySearch } from "../../../lib/articleSearch";
import type { NewsItem } from "../../../mocks/news";
import type { Article } from "../../../hooks/useArticles";
import type { Category } from "../../../hooks/useCategories";
import NewsCard from "../../home/components/NewsCard";

interface ArticleGridProps {
  activeCategory: string;
  search: string;
  articles: Article[];
  loading: boolean;
  categories: Category[];
}

function getItemSection(item: NewsItem): string {
  const cat = item.category || "";
  if (!cat || cat.toLowerCase() === "actualidad") return "General";
  return cat;
}

function groupBySection(items: NewsItem[]): Record<string, NewsItem[]> {
  const groups: Record<string, NewsItem[]> = {};
  items.forEach((item) => {
    const section = getItemSection(item);
    if (!groups[section]) groups[section] = [];
    groups[section].push(item);
  });
  return groups;
}

export default function ArticleGrid({ activeCategory, search, articles, loading, categories }: ArticleGridProps) {
  const [page, setPage] = useState(1);
  const perPage = 9;

  const allItems = useMemo(() => toNewsItems(articles), [articles]);

  // Map de nombre de categoría a sort_order para ordenar secciones
  const categoryOrder = useMemo(() => {
    const map: Record<string, number> = {};
    categories.forEach((c) => {
      map[c.name] = c.sort_order;
    });
    map["General"] = 999;
    return map;
  }, [categories]);

  const articlesById = useMemo(() => new Map(articles.map((a) => [a.id, a])), [articles]);
  const isSearching = search.trim().length > 0;

  // Mostrar todas las secciones (sin categoría activa ni búsqueda) o una lista plana
  const showAllSections = !activeCategory && !isSearching;

  // Filtrado por categoría activa
  const filteredByCat = useMemo(() => {
    if (!activeCategory) return allItems;
    return allItems.filter((a) =>
      matchesCategory(
        a.category || "",
        Array.isArray(a.tags) ? a.tags : [],
        activeCategory,
      ),
    );
  }, [allItems, activeCategory]);

  // Filtro de búsqueda: título, resumen, contenido, categorías, etiquetas, autor, fecha…
  const filtered = useMemo(
    () => filterBySearch(filteredByCat, search, (item) => articlesById.get(item.id)),
    [filteredByCat, search, articlesById],
  );

  // Vista agrupada por secciones cuando mostramos todas
  const groupedSections = useMemo(() => {
    if (!showAllSections) return null;
    const groups = groupBySection(filtered);
    const sections = Object.keys(groups).sort((a, b) => {
      return (categoryOrder[a] ?? 999) - (categoryOrder[b] ?? 999);
    });
    return sections.map((section) => ({
      section,
      items: groups[section].slice(0, 3),
      total: groups[section].length,
    }));
  }, [filtered, showAllSections, categoryOrder]);

  const paginated = filtered.slice(0, page * perPage);
  const hasMore = paginated.length < filtered.length;

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
          <div key={i} className="h-80 bg-slate-200 rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="w-14 h-14 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-4">
          <i className="ri-newspaper-line text-slate-400 text-2xl" />
        </div>
        {isSearching ? (
          <>
            <p className="text-slate-400 font-inter text-base">No se encontraron noticias para “{search.trim()}”.</p>
            <p className="text-slate-300 font-inter text-sm mt-1">Prueba con otras palabras, un autor, una categoría o una fecha.</p>
          </>
        ) : (
          <>
            <p className="text-slate-400 font-inter text-base">No hay artículos publicados en esta categoría.</p>
            <p className="text-slate-300 font-inter text-sm mt-1">Próximamente el equipo publicará contenido aquí.</p>
          </>
        )}
      </div>
    );
  }

  // Vista por secciones (todas las categorías)
  if (groupedSections) {
    return (
      <div className="space-y-12">
        {groupedSections.map(({ section, items, total }) => (
          <section key={section} className="relative">
            {/* Header de sección */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-1.5 h-6 bg-blue-600 rounded-full" />
                <h2
                  className="text-[#0B1F3A] font-bold uppercase tracking-wide"
                  style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontSize: "1.25rem",
                  }}
                >
                  {section}
                </h2>
                <span className="text-slate-400 text-xs font-inter">
                  {total} noticia{total !== 1 ? "s" : ""}
                </span>
              </div>
              <Link
                to={`/actualidad?categoria=${encodeURIComponent(section)}`}
                className="flex items-center gap-1 text-blue-600 text-sm font-inter font-semibold hover:text-blue-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Ver más <i className="ri-arrow-right-line" />
              </Link>
            </div>

            {/* Grid de noticias de esta sección */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((item) => (
                <NewsCard key={item.id} item={item} variant="default" />
              ))}
            </div>

            {/* Divider entre secciones */}
            <div className="mt-10 border-t border-slate-100" />
          </section>
        ))}
      </div>
    );
  }

  // Vista filtrada por una categoría específica
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {paginated.map((item) => (
          <NewsCard key={item.id} item={item} variant="default" />
        ))}
      </div>

      {hasMore && (
        <div className="mt-10 text-center">
          <button
            onClick={() => setPage((p) => p + 1)}
            className="px-8 py-3 border border-blue-300 text-blue-600 font-inter font-semibold text-sm rounded-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-2" />
            Cargar más artículos
          </button>
        </div>
      )}

      <p className="text-center text-slate-400 text-xs font-inter mt-6">
        Mostrando {Math.min(paginated.length, filtered.length)} de {filtered.length} artículos
      </p>
    </div>
  );
}